// frontend/src/pages/StatisticsPage.js

import React, { useState, useEffect } from 'react';
import api from '../api'; 
import { Modal, Button, Table, Spinner, Card, Row, Col, Form, Alert, ProgressBar } from 'react-bootstrap';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

// 注册 Chart.js 组件
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const StatisticsPage = () => {
  // ======= 总账目 =======
  const [ledger, setLedger] = useState(null);
  const [showLedgerModal, setShowLedgerModal] = useState(false);

  // ======= 游戏记录明细 =======
  const [records, setRecords] = useState([]);
  const [showRecordsModal, setShowRecordsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // ======= 班次账目 (白班/夜班) =======
  const [dayNightData, setDayNightData] = useState([]);
  const [dayNightPage, setDayNightPage] = useState(1);
  const [dayNightTotalPages, setDayNightTotalPages] = useState(1);
  const [showDayNightModal, setShowDayNightModal] = useState(false);

  // ======= 管理员上下分明细 =======
  const [adminLogs, setAdminLogs] = useState([]);
  const [adminLogPage, setAdminLogPage] = useState(1);
  const [adminLogTotalPages, setAdminLogTotalPages] = useState(1);
  const [showAdminLogsModal, setShowAdminLogsModal] = useState(false);

  // ======= 管理员充值退分汇总 =======
  const [adminLedger, setAdminLedger] = useState(null);
  const [showAdminLedgerModal, setShowAdminLedgerModal] = useState(false);

  // ======= 管理员班次上下分 =======
  const [adminDayNightData, setAdminDayNightData] = useState([]);
  const [adminDayNightPage, setAdminDayNightPage] = useState(1);
  const [adminDayNightTotalPages, setAdminDayNightTotalPages] = useState(1);
  const [showAdminDayNightModal, setShowAdminDayNightModal] = useState(false);

  // ======= 新增功能状态 =======
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30秒
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [showCharts, setShowCharts] = useState(false);
  const [chartData, setChartData] = useState(null);
  
  // 加载进度
  const [loadingProgress, setLoadingProgress] = useState(0);

  // 删除所有日志（逻辑不变）
  const handleDeleteAllLogs = async () => {
    try {
      const inputPwd = prompt('请输入密码以删除所有日志:');
      if (!inputPwd) return;
      if (inputPwd !== '147208482') {
        showMessage('密码错误，无法删除日志！', 'error');
        return;
      }
      if (!window.confirm('确认要删除所有日志吗？此操作无法撤销！')) return;

      setLoading(true);
      simulateProgress();
      await api.delete('/api/admin/statistics/allLogs', {
        data: { password: inputPwd },
      });
      showMessage('所有日志已删除！', 'success');
    } catch (err) {
      console.error('删除日志失败:', err);
      showMessage(err.response?.data?.message || '删除日志失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 1) 获取总账目
  const handleGetLedger = React.useCallback(async (showLoadingUI = true) => {
    try {
      if (showLoadingUI) {
        setLoading(true);
        simulateProgress();
      }
      const res = await api.get('/api/admin/statistics/ledger'); 
      
      // 修正利润符号（如果后端计算有误）
      const correctedLedger = {
        ...res.data,
        profit: res.data.totalCredit - res.data.totalDebit  // 前端重新计算：收入 - 支出
      };
      
      setLedger(correctedLedger);
      if (showLoadingUI) {
        setShowLedgerModal(true);
        showMessage('总账目获取成功', 'success');
      }
    } catch (err) {
      console.error('获取总账目失败:', err);
      showMessage(err.response?.data?.message || '获取总账目失败', 'error');
    } finally {
      if (showLoadingUI) setLoading(false);
    }
  }, []);

  // 2) 获取游戏记录明细(分页)
  const handleGetRecords = async (page = 1) => {
    try {
      setLoading(true);
      simulateProgress();
      const limit = 10;
      let url = `/api/admin/statistics/records?page=${page}&limit=${limit}`;
      
      // 添加日期筛选
      if (dateFilter.startDate) {
        url += `&startDate=${dateFilter.startDate}`;
      }
      if (dateFilter.endDate) {
        url += `&endDate=${dateFilter.endDate}`;
      }
      
      const res = await api.get(url);
      
      // 修正游戏记录的利润计算
      const correctedRecords = res.data.records.map(record => ({
        ...record,
        netProfit: record.betTotal - record.payoutTotal  // 前端重新计算利润
      }));
      
      setRecords(correctedRecords);
      setCurrentPage(res.data.currentPage);
      setTotalPages(res.data.totalPages);
      setShowRecordsModal(true);
      showMessage('游戏记录获取成功', 'success');
    } catch (err) {
      console.error('获取明细失败:', err);
      showMessage(err.response?.data?.message || '获取明细失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 3) 获取班次账目 (白班 / 夜班)
  const handleGetDayNightLedger = async (page = 1) => {
    try {
      setLoading(true);
      simulateProgress();
      const limit = 10;
      const res = await api.get(`/api/admin/statistics/dayNightLedger?page=${page}&limit=${limit}`);
      
      // 修正班次数据的利润计算
      const correctedDayNightData = res.data.data.map(shift => ({
        ...shift,
        profit: shift.totalCredit - shift.totalDebit  // 前端重新计算利润
      }));
      
      setDayNightData(correctedDayNightData);
      setDayNightPage(res.data.currentPage);
      setDayNightTotalPages(res.data.totalPages);
      setShowDayNightModal(true);
      
      // 生成图表数据
      setChartData(generateChartData(correctedDayNightData));
      
      showMessage('班次账目获取成功', 'success');
    } catch (err) {
      console.error('获取班次账目失败:', err);
      showMessage(err.response?.data?.message || '获取班次账目失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 4) 获取管理员上下分明细(分页)
  const handleGetAdminLogs = async (page = 1) => {
    try {
      setLoading(true);
      simulateProgress();
      const limit = 10;
      const res = await api.get(`/api/admin/statistics/adminScoreLogs?page=${page}&limit=${limit}`);
      setAdminLogs(res.data.logs);
      setAdminLogPage(res.data.currentPage);
      setAdminLogTotalPages(res.data.totalPages);
      setShowAdminLogsModal(true);
      showMessage('管理员操作记录获取成功', 'success');
    } catch (err) {
      console.error('获取管理员上下分明细失败:', err);
      showMessage(err.response?.data?.message || '获取管理员上下分明细失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 5) 获取管理员充值退分汇总
  const handleGetAdminLedger = async () => {
    try {
      setLoading(true);
      simulateProgress();
      const res = await api.get('/api/admin/statistics/adminLedger');
      setAdminLedger(res.data);
      setShowAdminLedgerModal(true);
      showMessage('管理员统计获取成功', 'success');
    } catch (err) {
      console.error('获取管理员充值退分汇总失败:', err);
      showMessage(err.response?.data?.message || '获取管理员充值退分汇总失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 6) 获取管理员班次上下分 (白班/夜班)
  const handleGetAdminDayNightLedger = async (page = 1) => {
    try {
      setLoading(true);
      simulateProgress();
      const limit = 10;
      const res = await api.get(`/api/admin/statistics/adminDayNightLedger?page=${page}&limit=${limit}`);
      setAdminDayNightData(res.data.data);
      setAdminDayNightPage(res.data.currentPage);
      setAdminDayNightTotalPages(res.data.totalPages);
      setShowAdminDayNightModal(true);
      showMessage('管理员班次统计获取成功', 'success');
    } catch (err) {
      console.error('获取管理员班次账目失败:', err);
      showMessage(err.response?.data?.message || '获取管理员班次账目失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ======= 自动刷新功能 =======
  useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(() => {
        handleGetLedger(false); // 静默刷新，不显示loading
      }, refreshInterval);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, refreshInterval, handleGetLedger]);

  // ======= 工具函数 =======
  const showMessage = (message, type = 'error') => {
    if (type === 'error') {
      setError(message);
      setTimeout(() => setError(''), 5000);
    } else {
      setSuccess(message);
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const simulateProgress = () => {
    setLoadingProgress(0);
    const increment = 100 / 20; // 20步完成
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        const next = prev + increment;
        if (next >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return next;
      });
    }, 50);
  };

  // 生成图表数据
  const generateChartData = (data) => {
    if (!data || data.length === 0) return null;
    
    const labels = data.map(item => {
      if (item._id?.date && item._id?.shift) {
        return `${item._id.date} ${item._id.shift === 'DAY_SHIFT' ? '白班' : '夜班'}`;
      }
      return '未知';
    });
    
    return {
      labels,
      datasets: [
        {
          label: '总投注',
          data: data.map(item => item.totalCredit || 0),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 2,
        },
        {
          label: '总赔付',
          data: data.map(item => item.totalDebit || 0),
          backgroundColor: 'rgba(255, 99, 132, 0.6)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 2,
        },
        {
          label: '净利润',
          data: data.map(item => item.profit || 0),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 2,
        }
      ]
    };
  };

  // 导出数据为CSV
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      showMessage('没有数据可导出', 'error');
      return;
    }

    const headers = Object.keys(data[0]).join(',');
    const csvContent = [
      headers,
      ...data.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showMessage('数据导出成功', 'success');
  };

  return (
    <div style={styles.pageContainer}>
      {/* 消息提示 */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {/* 主标题卡片 */}
      <Card className="mb-4" style={styles.headerCard}>
        <Card.Body>
          <Row className="align-items-center">
            <Col>
              <h2 className="mb-0" style={styles.title}>📊 数据统计中心</h2>
              <p className="mb-0 text-muted">实时查看系统运营数据和统计信息</p>
            </Col>
            <Col xs="auto">
              {/* 自动刷新控制 */}
              <Form.Check
                type="switch"
                id="auto-refresh-switch"
                label={`自动刷新 (${refreshInterval/1000}s)`}
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="mb-2"
              />
              <Form.Select
                size="sm"
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                disabled={!autoRefresh}
                style={{ minWidth: '120px' }}
              >
                <option value={10000}>10秒</option>
                <option value={30000}>30秒</option>
                <option value={60000}>1分钟</option>
                <option value={300000}>5分钟</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 加载状态 */}
      {loading && (
        <Card className="mb-4">
          <Card.Body>
            <div className="text-center">
              <Spinner animation="border" role="status" className="me-2">
                <span className="visually-hidden">加载中...</span>
              </Spinner>
              <span>数据加载中...</span>
            </div>
            <ProgressBar 
              now={loadingProgress} 
              className="mt-2" 
              animated 
              variant="info"
            />
          </Card.Body>
        </Card>
      )}

      {/* 日期筛选卡片 */}
      <Card className="mb-4" style={styles.filterCard}>
        <Card.Header>
          <h5 className="mb-0">🗓️ 筛选条件</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>开始日期</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFilter.startDate}
                  onChange={(e) => setDateFilter(prev => ({...prev, startDate: e.target.value}))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>结束日期</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFilter.endDate}
                  onChange={(e) => setDateFilter(prev => ({...prev, endDate: e.target.value}))}
                />
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button 
                variant="outline-secondary"
                onClick={() => setDateFilter({startDate: '', endDate: ''})}
                className="me-2"
              >
                清除筛选
              </Button>
              <Button
                variant="info"
                onClick={() => setShowCharts(!showCharts)}
              >
                {showCharts ? '隐藏图表' : '显示图表'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 功能按钮卡片 */}
      <Row className="mb-4">
        <Col lg={8}>
          <Card style={styles.actionCard}>
            <Card.Header>
              <h5 className="mb-0">⚡ 快速操作</h5>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={6} lg={4}>
                  <Button 
                    variant="primary" 
                    onClick={() => handleGetLedger(true)}
                    className="w-100"
                    style={styles.actionButton}
                    disabled={loading}
                  >
                    💰 总账目统计
                  </Button>
                </Col>
                <Col md={6} lg={4}>
                  <Button 
                    variant="secondary" 
                    onClick={() => handleGetRecords(1)}
                    className="w-100"
                    style={styles.actionButton}
                    disabled={loading}
                  >
                    📝 游戏记录
                  </Button>
                </Col>
                <Col md={6} lg={4}>
                  <Button 
                    variant="warning" 
                    onClick={() => handleGetDayNightLedger(1)}
                    className="w-100"
                    style={styles.actionButton}
                    disabled={loading}
                  >
                    🌅 班次账目
                  </Button>
                </Col>
                <Col md={6} lg={4}>
                  <Button 
                    variant="dark" 
                    onClick={() => handleGetAdminLogs(1)}
                    className="w-100"
                    style={styles.actionButton}
                    disabled={loading}
                  >
                    👨‍💼 管理员操作
                  </Button>
                </Col>
                <Col md={6} lg={4}>
                  <Button 
                    variant="success" 
                    onClick={handleGetAdminLedger}
                    className="w-100"
                    style={styles.actionButton}
                    disabled={loading}
                  >
                    📈 管理员统计
                  </Button>
                </Col>
                <Col md={6} lg={4}>
                  <Button 
                    variant="outline-success" 
                    onClick={() => handleGetAdminDayNightLedger(1)}
                    className="w-100"
                    style={styles.actionButton}
                    disabled={loading}
                  >
                    🌙 管理员班次
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card style={styles.dangerCard}>
            <Card.Header className="bg-danger text-white">
              <h5 className="mb-0">⚠️ 危险操作</h5>
            </Card.Header>
            <Card.Body>
              <Button 
                variant="danger" 
                onClick={handleDeleteAllLogs}
                className="w-100"
                disabled={loading}
              >
                🗑️ 删除所有日志
              </Button>
              <small className="text-muted d-block mt-2">
                此操作不可撤销，请谨慎操作
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 图表展示 */}
      {showCharts && chartData && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">📈 数据可视化</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col lg={8}>
                <div style={{ height: '400px' }}>
                  <Bar 
                    data={chartData} 
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top',
                        },
                        title: {
                          display: true,
                          text: '班次账目对比图'
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true
                        }
                      }
                    }} 
                  />
                </div>
              </Col>
              <Col lg={4}>
                {ledger && (
                  <div style={{ height: '400px' }}>
                    <Doughnut 
                      data={{
                        labels: ['总投注', '总赔付'],
                        datasets: [{
                          data: [ledger.totalCredit, ledger.totalDebit],
                          backgroundColor: [
                            'rgba(54, 162, 235, 0.8)',
                            'rgba(255, 99, 132, 0.8)'
                          ],
                          borderColor: [
                            'rgba(54, 162, 235, 1)',
                            'rgba(255, 99, 132, 1)'
                          ],
                          borderWidth: 2
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'bottom',
                          },
                          title: {
                            display: true,
                            text: '总账目分布'
                          }
                        }
                      }}
                    />
                  </div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* ============= 总账目弹窗 ============= */}
      <Modal show={showLedgerModal} onHide={() => setShowLedgerModal(false)} size="lg">
        <Modal.Header closeButton style={styles.modalHeader}>
          <Modal.Title>💰 总账目统计</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {ledger ? (
            <>
              <Row className="mb-3">
                <Col>
                  <Alert variant="info">
                    <strong>📊 统计说明：</strong> 基于游戏轮次记录统计，投注为上分，赔付为下分
                  </Alert>
                </Col>
              </Row>
              <Row className="mb-4">
                <Col md={4}>
                  <Card className="text-center border-primary">
                    <Card.Body>
                      <h5 className="text-primary">总投注</h5>
                      <h3 className="text-primary">{ledger.totalCredit.toLocaleString()}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-danger">
                    <Card.Body>
                      <h5 className="text-danger">总赔付</h5>
                      <h3 className="text-danger">{ledger.totalDebit.toLocaleString()}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-success">
                    <Card.Body>
                      <h5 className="text-success">净利润</h5>
                      <h3 className="text-success">{ledger.profit.toLocaleString()}</h3>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Table bordered hover style={styles.enhancedTable}>
                <thead className="table-dark">
                  <tr>
                    <th>项目</th>
                    <th>金额</th>
                    <th>占比</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>总投注</td>
                    <td className="text-primary fw-bold">{ledger.totalCredit.toLocaleString()}</td>
                    <td>{ledger.totalCredit > 0 ? '100%' : '0%'}</td>
                  </tr>
                  <tr>
                    <td>总赔付</td>
                    <td className="text-danger fw-bold">{ledger.totalDebit.toLocaleString()}</td>
                    <td>
                      {ledger.totalCredit > 0 
                        ? `${((ledger.totalDebit / ledger.totalCredit) * 100).toFixed(1)}%`
                        : '0%'
                      }
                    </td>
                  </tr>
                  <tr className="table-success">
                    <td className="fw-bold">净利润</td>
                    <td className="text-success fw-bold">{ledger.profit.toLocaleString()}</td>
                    <td className="fw-bold">
                      {ledger.totalCredit > 0 
                        ? `${((ledger.profit / ledger.totalCredit) * 100).toFixed(1)}%`
                        : '0%'
                      }
                    </td>
                  </tr>
                </tbody>
              </Table>
            </>
          ) : (
            <Alert variant="warning">暂无数据</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          {ledger && (
            <Button 
              variant="outline-primary" 
              onClick={() => exportToCSV([ledger], '总账目统计')}
              className="me-2"
            >
              📥 导出数据
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowLedgerModal(false)}>
            关闭
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============= 游戏记录明细弹窗 ============= */}
      <Modal show={showRecordsModal} onHide={() => setShowRecordsModal(false)} size="xl">
        <Modal.Header closeButton style={styles.modalHeader}>
          <Modal.Title>📝 游戏记录明细</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* 工具栏 */}
          <Row className="mb-3">
            <Col>
              <Alert variant="info">
                <strong>📋 数据说明：</strong> 显示基于轮次的游戏记录，包含投注、赔付和净利润信息
              </Alert>
            </Col>
          </Row>
          
          {/* 分页和导出按钮 */}
          <Row className="mb-3">
            <Col className="d-flex justify-content-between align-items-center">
              <div>
                <Button
                  variant="outline-primary"
                  disabled={currentPage <= 1}
                  onClick={() => handleGetRecords(currentPage - 1)}
                  className="me-2"
                >
                  ⬅️ 上一页
                </Button>
                <Button
                  variant="outline-primary"
                  disabled={currentPage >= totalPages}
                  onClick={() => handleGetRecords(currentPage + 1)}
                  className="me-2"
                >
                  下一页 ➡️
                </Button>
                <span className="text-muted">第 {currentPage} / {totalPages} 页</span>
              </div>
              <Button 
                variant="success" 
                onClick={() => exportToCSV(records, '游戏记录明细')}
                disabled={!records || records.length === 0}
              >
                📥 导出当前页
              </Button>
            </Col>
          </Row>

          {records && records.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <Table bordered hover size="sm" style={styles.enhancedTable}>
                <thead className="table-dark">
                  <tr>
                    <th>投注金额</th>
                    <th>赔付金额</th>
                    <th>净利润</th>
                    <th>开始时间</th>
                    <th>结束时间</th>
                    <th>用户</th>
                    <th>设备</th>
                    <th>状态</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => {
                    const userName = r.playerId?.username || '未知用户';
                    const deviceName = r.deviceId?.name || '未知设备';
                    const endTime = r.endTime ? new Date(r.endTime).toLocaleString() : '进行中';
                    const isActive = !r.endTime;
                    const profitClass = r.netProfit > 0 ? 'text-success' : r.netProfit < 0 ? 'text-danger' : 'text-muted';
                    
                    return (
                      <tr key={r._id} className={isActive ? 'table-warning' : ''}>
                        <td className="text-primary fw-bold">{r.betTotal.toLocaleString()}</td>
                        <td className="text-danger fw-bold">{r.payoutTotal.toLocaleString()}</td>
                        <td className={`fw-bold ${profitClass}`}>{r.netProfit.toLocaleString()}</td>
                        <td>{new Date(r.startTime).toLocaleString()}</td>
                        <td>{isActive ? <span className="badge bg-warning">进行中</span> : endTime}</td>
                        <td>{userName}</td>
                        <td>{deviceName}</td>
                        <td>
                          {isActive ? (
                            <span className="badge bg-warning">活跃</span>
                          ) : (
                            <span className="badge bg-success">已完成</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            <Alert variant="warning">暂无记录</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRecordsModal(false)}>关闭</Button>
        </Modal.Footer>
      </Modal>

      {/* ============= 班次账目弹窗 ============= */}
      <Modal show={showDayNightModal} onHide={() => setShowDayNightModal(false)} size="xl">
        <Modal.Header closeButton style={styles.modalHeader}>
          <Modal.Title>🌅 班次账目统计 (白班 9:00-21:00 / 夜班 21:00-9:00)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col>
              <Alert variant="info">
                <strong>📊 统计说明：</strong> 按班次统计游戏账目，白班(9:00-21:00)，夜班(21:00-次日9:00)
              </Alert>
            </Col>
          </Row>

          {/* 分页和导出按钮 */}
          <Row className="mb-3">
            <Col className="d-flex justify-content-between align-items-center">
              <div>
                <Button
                  variant="outline-primary"
                  disabled={dayNightPage <= 1}
                  onClick={() => handleGetDayNightLedger(dayNightPage - 1)}
                  className="me-2"
                >
                  ⬅️ 上一页
                </Button>
                <Button
                  variant="outline-primary"
                  disabled={dayNightPage >= dayNightTotalPages}
                  onClick={() => handleGetDayNightLedger(dayNightPage + 1)}
                  className="me-2"
                >
                  下一页 ➡️
                </Button>
                <span className="text-muted">第 {dayNightPage} / {dayNightTotalPages} 页</span>
              </div>
              <Button 
                variant="success" 
                onClick={() => exportToCSV(dayNightData, '班次账目统计')}
                disabled={!dayNightData || dayNightData.length === 0}
              >
                📥 导出数据
              </Button>
            </Col>
          </Row>

          {dayNightData && dayNightData.length > 0 ? (
            <>
              {/* 图表展示 */}
              {chartData && (
                <Card className="mb-4">
                  <Card.Header>
                    <h6 className="mb-0">📈 班次对比图表</h6>
                  </Card.Header>
                  <Card.Body>
                    <div style={{ height: '300px' }}>
                      <Bar 
                        data={chartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'top',
                            },
                            title: {
                              display: true,
                              text: '班次账目对比'
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return value.toLocaleString();
                                }
                              }
                            }
                          }
                        }} 
                      />
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* 数据表格 */}
              <Table bordered hover size="sm" style={styles.enhancedTable}>
                <thead className="table-dark">
                  <tr>
                    <th>日期 + 班次</th>
                    <th>总投注</th>
                    <th>总赔付</th>
                    <th>净利润</th>
                    <th>利润率</th>
                  </tr>
                </thead>
                <tbody>
                  {dayNightData.map(shift => {
                    const dateStr = shift._id.date;
                    const shiftStr = shift._id.shift === 'DAY_SHIFT' ? '🌅 白班' : '🌙 夜班';
                    const profitRate = shift.totalCredit > 0 
                      ? ((shift.profit / shift.totalCredit) * 100).toFixed(1) 
                      : '0.0';
                    const profitClass = shift.profit > 0 ? 'text-success' : shift.profit < 0 ? 'text-danger' : 'text-muted';
                    const rowClass = shift._id.shift === 'DAY_SHIFT' ? 'table-light' : 'table-secondary';
                    
                    return (
                      <tr key={`${dateStr}-${shiftStr}`} className={rowClass}>
                        <td className="fw-bold">{dateStr} {shiftStr}</td>
                        <td className="text-primary fw-bold">{shift.totalCredit.toLocaleString()}</td>
                        <td className="text-danger fw-bold">{shift.totalDebit.toLocaleString()}</td>
                        <td className={`fw-bold ${profitClass}`}>{shift.profit.toLocaleString()}</td>
                        <td className={`fw-bold ${profitClass}`}>{profitRate}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </>
          ) : (
            <Alert variant="warning">暂无记录</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDayNightModal(false)}>
            关闭
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============= 管理员上下分明细弹窗 ============= */}
      <Modal show={showAdminLogsModal} onHide={() => setShowAdminLogsModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>管理员上下分明细</Modal.Title></Modal.Header>
        <Modal.Body>
          {/* 分页按钮 */}
          <div style={{ marginBottom: '10px' }}>
            <Button
              variant="outline-primary"
              disabled={adminLogPage <= 1}
              onClick={() => handleGetAdminLogs(adminLogPage - 1)}
              style={{ marginRight: 10 }}
            >
              上一页
            </Button>
            <Button
              variant="outline-primary"
              disabled={adminLogPage >= adminLogTotalPages}
              onClick={() => handleGetAdminLogs(adminLogPage + 1)}
            >
              下一页
            </Button>
            <span style={{ marginLeft: 10 }}>
              第 {adminLogPage} / {adminLogTotalPages} 页
            </span>
          </div>

          {adminLogs && adminLogs.length > 0 ? (
            <Table bordered hover>
              <thead>
                <tr>
                  <th>时间</th>
                  <th>操作类型</th>
                  <th>积分</th>
                  <th>管理员</th>
                  <th>目标用户</th>
                </tr>
              </thead>
              <tbody>
                {adminLogs.map(log => {
                  const timeStr = new Date(log.createdAt).toLocaleString();
                  const operatorName = log.operatorId?.username || '未知管理员';
                  const targetName   = log.targetUserId?.username || '未知用户';
                  let typeZh = '';
                  if (log.operationType === 'admin_add_points') typeZh = '管理员加分';
                  else if (log.operationType === 'admin_deduct_points') typeZh = '管理员减分';
                  else typeZh = log.operationType; // 其他操作

                  return (
                    <tr key={log._id}>
                      <td>{timeStr}</td>
                      <td>{typeZh}</td>
                      <td>{log.points}</td>
                      <td>{operatorName}</td>
                      <td>{targetName}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : <p>暂无记录</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdminLogsModal(false)}>
            关闭
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============= 管理员充值退分汇总弹窗 ============= */}
      <Modal show={showAdminLedgerModal} onHide={() => setShowAdminLedgerModal(false)}>
        <Modal.Header closeButton><Modal.Title>管理员充值退分汇总</Modal.Title></Modal.Header>
        <Modal.Body>
          {adminLedger ? (
            <Table bordered hover>
              <thead>
                <tr><th>管理员总充值</th><th>管理员总退分</th><th>盈利</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>{adminLedger.totalAdd}</td>
                  <td>{adminLedger.totalDeduct}</td>
                  <td>{adminLedger.profit}</td>
                </tr>
              </tbody>
            </Table>
          ) : <p>暂无数据</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdminLedgerModal(false)}>
            关闭
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============= 管理员班次上下分(白班/夜班) ============= */}
      <Modal
        show={showAdminDayNightModal}
        onHide={() => setShowAdminDayNightModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>管理员班次上下分 (白班 / 夜班)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* 翻页按钮 */}
          <div style={{ marginBottom: '10px' }}>
            <Button
              variant="outline-primary"
              disabled={adminDayNightPage <= 1}
              onClick={() => handleGetAdminDayNightLedger(adminDayNightPage - 1)}
              style={{ marginRight: 10 }}
            >
              上一页
            </Button>
            <Button
              variant="outline-primary"
              disabled={adminDayNightPage >= adminDayNightTotalPages}
              onClick={() => handleGetAdminDayNightLedger(adminDayNightPage + 1)}
            >
              下一页
            </Button>
            <span style={{ marginLeft: 10 }}>
              第 {adminDayNightPage} / {adminDayNightTotalPages} 页
            </span>
          </div>

          {adminDayNightData && adminDayNightData.length > 0 ? (
            <Table bordered hover>
              <thead>
                <tr>
                  <th>日期 + 班次</th>
                  <th>管理员总加分</th>
                  <th>管理员总退分</th>
                  <th>盈利</th>
                </tr>
              </thead>
              <tbody>
                {adminDayNightData.map(shift => {
                  const dateStr = shift._id.date;
                  const shiftStr = (shift._id.shift === 'DAY_SHIFT') ? '白班' : '夜班';
                  return (
                    <tr key={`${dateStr}-${shiftStr}`}>
                      <td>{dateStr} {shiftStr}</td>
                      <td>{shift.totalAdd}</td>
                      <td>{shift.totalDeduct}</td>
                      <td>{shift.profit}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : <p>暂无数据</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdminDayNightModal(false)}>
            关闭
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// 简单的内联样式，也可抽到一个 styles.js
const styles = {
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    padding: '20px',
  },
  headerCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
  },
  title: {
    fontWeight: '600',
    fontSize: '1.8rem',
  },
  filterCard: {
    border: 'none',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    borderRadius: '12px',
  },
  actionCard: {
    border: 'none',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    borderRadius: '12px',
  },
  dangerCard: {
    border: 'none',
    boxShadow: '0 4px 20px rgba(220, 53, 69, 0.2)',
    borderRadius: '12px',
  },
  actionButton: {
    borderRadius: '8px',
    fontWeight: '500',
    padding: '12px 20px',
    transition: 'all 0.3s ease',
    border: 'none',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  // 模态框样式增强
  enhancedModal: {
    borderRadius: '15px',
    border: 'none',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
  },
  modalHeader: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    borderTopLeftRadius: '15px',
    borderTopRightRadius: '15px',
    border: 'none',
  },
  // 表格样式
  enhancedTable: {
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
  },
  // 废弃的旧样式保留兼容性
  card: {
    maxWidth: '800px',
    margin: '0 auto',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    padding: '20px',
  },
  buttonArea: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    justifyContent: 'center',
    marginBottom: '20px',
  },
};

export default StatisticsPage;